/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { compact } from 'lodash-es';
import type { ApolloError } from '@apollo/client';
import type { ServerError } from '@apollo/client/link/utils';
import type { GraphQLFormattedError } from 'graphql';
import { serverResponseErrorsCodes } from './consts';

const isApolloError = (error: unknown): error is ApolloError =>
  error instanceof Error && 'graphQLErrors' in error && 'networkError' in error;

const isServerError = (error: unknown): error is ServerError =>
  error instanceof Error && 'statusCode' in error && 'result' in error;

interface HasExtensions {
  extensions?: Record<string, unknown>;
}

const hasExtensions = (value: unknown): value is HasExtensions =>
  typeof value === 'object' && value !== null && 'extensions' in value;

/* ========== Error code extractors ========== */

const extractNetworkResultErrorCodes = (networkError: unknown): string[] => {
  if (
    isServerError(networkError) &&
    networkError.result !== null &&
    typeof networkError.result === 'object'
  ) {
    const { errors } = networkError.result as Record<string, unknown>;
    if (Array.isArray(errors)) {
      return compact(
        errors.map((e: any) => e?.extensions?.code as string | undefined)
      );
    }
  }
  return [];
};

const extractGraphQLErrorCodes = (
  errors: readonly GraphQLFormattedError[]
): string[] =>
  compact(errors.map((e) => e.extensions?.code as string | undefined));

const getErrorCodesOfServerResponse = (serverError: unknown): string[] => {
  if (Array.isArray(serverError)) {
    return compact(serverError.flatMap(getErrorCodesOfServerResponse));
  }

  if (isApolloError(serverError)) {
    return compact([
      ...extractNetworkResultErrorCodes(serverError.networkError),
      ...extractGraphQLErrorCodes(serverError.graphQLErrors),
    ]);
  }

  if (hasExtensions(serverError)) {
    return compact([serverError.extensions?.code as string | undefined]);
  }

  return [];
};

export const isServerErrorOfType = ({
  error,
  type,
}: {
  error: unknown;
  type: serverResponseErrorsCodes;
}) => getErrorCodesOfServerResponse(error).some((er) => er === type);
